/**
 * Cova Chat — authenticated-user RPC contract (UserMiddleware). The broker ⇄
 * underwriter messaging surface: start-or-get a conversation, send a message
 * (with attachments), list the viewer-scoped inbox + a thread page, mark a
 * thread read, and the nav-bell unread signal.
 *
 * Mailbox ids NEVER appear on the wire — addressing is by owner coordinates
 * (an underwriter id) and every read is keyed server-side on the caller's
 * serviceable mailbox set (spec §4, §8, §9).
 */
import { Rpc, RpcGroup } from "@effect/rpc"
import { AllowedApps } from "@/presentation/rpc/middleware/allowed-apps"
import {
  AuditActorMiddleware,
  FilesViewerMiddleware,
} from "@/contexts/files/presentation/activity-actor-middleware"
import { Schema as S } from "effect"
import { AuthorisationError } from "@/application/shared/schemas/authorisation-schemas"
import { UnderwriterId } from "@/contexts/underwriters"
import { UserMiddleware } from "@/presentation/rpc/middleware/user-middleware"
import { TenancyMiddleware } from "@/presentation/rpc/middleware/tenancy-middleware"
import {
  CallId,
  CallLogView,
  MeetingId,
  MeetingView,
  ScheduledMeetingView,
  RecurrencePreset,
  MeetingJoinInfo,
  TranscriptSegmentView,
  TranscriptSegmentInput,
  ChatMessageId,
  ChatRpcError,
  ChatUnreadStatus,
  ChatTeammate,
  ConversationAttachmentView,
  ConversationProfileView,
  ConversationId,
  ConversationSummaryView,
  GroupMemberView,
  MessagePage,
} from "../domain"
import { BillingLockoutMiddleware } from "@/presentation/rpc/middleware/billing-lockout-middleware"

import { RequestIdentityMiddleware } from "@/presentation/rpc/middleware/request-identity-middleware"
/** Every method fails with an authz error (from the middleware) or a chat error. */
const Err = S.Union(AuthorisationError, ChatRpcError)

/** Page size for the keyset list RPCs — a positive integer, capped so a caller
 *  can't request a fractional, negative, or huge page. */
const PageLimit = S.Int.pipe(S.greaterThanOrEqualTo(1), S.lessThanOrEqualTo(100))

/**
 * The addressing target for a broker-initiated thread. A discriminated union so
 * v1's single underwriter target can grow (e.g. a future person target) without
 * a breaking shape change.
 */
const ChatTarget = S.Union(
  S.Struct({ kind: S.Literal("underwriter"), underwriterId: UnderwriterId }),
  // Team chat: a same-org teammate (userId = users.id). Membership is verified
  // server-side; a non-member resolves to not_found.
  S.Struct({ kind: S.Literal("user"), userId: S.String }),
)

export const StartConversationRpc = Rpc.make("startConversation", {
  payload: S.Struct({ target: ChatTarget }),
  success: S.Struct({ conversationId: ConversationId }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

/**
 * Per-attachment metadata is carried whole (`uploadId` + `filename`/`mime`/
 * `sizeBytes`), NOT as bare upload-id strings: `UploadService.completeUpload`
 * returns only `{ nodeId, versionId }`, so the descriptive fields must come from
 * the client (which supplied them at prepareUpload). `filename` is PII — never
 * logged. Capped at 10 attachments; body 1..8000 chars.
 */
export const SendMessageRpc = Rpc.make("sendMessage", {
  payload: S.Struct({
    conversationId: ConversationId,
    body: S.String.pipe(S.minLength(1), S.maxLength(8000)),
    attachments: S.optional(
      S.Array(
        S.Struct({
          uploadId: S.String,
          filename: S.String,
          mime: S.String,
          sizeBytes: S.Number,
        }),
      ).pipe(S.maxItems(10)),
    ),
    // @member mentions: the mentioned members' users.id. Validated server-side
    // to be participants of the conversation (a non-participant fails). Capped at 50.
    mentions: S.optional(S.Array(S.String).pipe(S.maxItems(50))),
  }),
  success: S.Struct({ messageId: ChatMessageId }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

export const ListConversationsRpc = Rpc.make("listConversations", {
  payload: S.Struct({
    cursor: S.optional(S.NullOr(S.String)),
    limit: S.optional(PageLimit),
  }),
  success: S.Struct({
    items: S.Array(ConversationSummaryView),
    nextCursor: S.NullOr(S.String),
  }),
  error: Err,
})

export const ListMessagesRpc = Rpc.make("listMessages", {
  payload: S.Struct({
    conversationId: ConversationId,
    cursor: S.optional(S.NullOr(S.String)),
    limit: S.optional(PageLimit),
  }),
  success: MessagePage,
  error: Err,
})

export const MarkReadRpc = Rpc.make("markRead", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({ ok: S.Boolean, previousLastReadAt: S.NullOr(S.String) }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

export const GetChatUnreadRpc = Rpc.make("getChatUnread", {
  payload: S.Void,
  success: ChatUnreadStatus,
  error: Err,
})

// Lazily-signed, participant-scoped download URL for a message attachment. The
// frontend never gets a URL until the user clicks (URLs are short-lived).
export const GetAttachmentUrlRpc = Rpc.make("getAttachmentUrl", {
  payload: S.Struct({ conversationId: ConversationId, fileNodeId: S.String }),
  success: S.Struct({ url: S.String }),
  error: Err,
})

// The caller's teammates, for the team-chat "new message" picker.
export const ListTeammatesRpc = Rpc.make("listTeammates", {
  payload: S.Void,
  success: S.Array(ChatTeammate),
  error: Err,
})

// ─── Group chat (user-created groups) ──────────────────────────────────────

export const CreateGroupRpc = Rpc.make("createGroup", {
  payload: S.Struct({
    title: S.String.pipe(S.minLength(1), S.maxLength(120)),
    memberUserIds: S.Array(S.String).pipe(S.maxItems(200)),
  }),
  success: S.Struct({ conversationId: ConversationId }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

export const RenameGroupRpc = Rpc.make("renameGroup", {
  payload: S.Struct({
    conversationId: ConversationId,
    title: S.String.pipe(S.minLength(1), S.maxLength(120)),
  }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

export const AddGroupMembersRpc = Rpc.make("addGroupMembers", {
  payload: S.Struct({
    conversationId: ConversationId,
    memberUserIds: S.Array(S.String).pipe(S.minItems(1), S.maxItems(200)),
  }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

export const RemoveGroupMemberRpc = Rpc.make("removeGroupMember", {
  payload: S.Struct({ conversationId: ConversationId, memberUserId: S.String }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

export const LeaveGroupRpc = Rpc.make("leaveGroup", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

export const ListGroupMembersRpc = Rpc.make("listGroupMembers", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({ members: S.Array(GroupMemberView) }),
  error: Err,
})

// ─── Direct/Spaces redesign — mute + shared content ────────────────────────

// Toggle the caller's OWN mute flag on a conversation (silences in-app chat
// notifications; realtime push is unaffected).
export const SetChatMutedRpc = Rpc.make("setChatMuted", {
  payload: S.Struct({ conversationId: ConversationId, muted: S.Boolean }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

// Toggle the caller's OWN favourite (star) flag on a conversation — a favourited
// thread is pinned to the top of the caller's inbox.
export const SetChatFavouritedRpc = Rpc.make("setChatFavourited", {
  payload: S.Struct({ conversationId: ConversationId, favourited: S.Boolean }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

// Toggle the caller's OWN archive (hide) flag — an archived 1:1 is hidden from
// the caller's inbox until a new message resurfaces it.
export const SetChatArchivedRpc = Rpc.make("setChatArchived", {
  payload: S.Struct({ conversationId: ConversationId, archived: S.Boolean }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
}).middleware(BillingLockoutMiddleware)

// Every file shared in a conversation (the "Content" tab), newest-first.
export const ListConversationContentRpc = Rpc.make("listConversationContent", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({ attachments: S.Array(ConversationAttachmentView) }),
  error: Err,
})

// Extra contact details for a 1:1 counterparty (the "Profile" tab): email,
// contact number, role, org. PII — fetched on demand, never in the inbox list.
export const GetConversationProfileRpc = Rpc.make("getConversationProfile", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: ConversationProfileView,
  error: Err,
})

/**
 * Presence for the visible 1:1 conversations — online if the counterparty (or,
 * for an underwriter firm, ANY servicing member) has a live chat stream. Pass
 * DIRECT conversationIds only; groups/channels have no counterparty presence.
 */
export const GetPresenceRpc = Rpc.make("getPresence", {
  payload: S.Struct({ conversationIds: S.Array(ConversationId).pipe(S.maxItems(50)) }),
  success: S.Struct({
    presence: S.Array(S.Struct({ conversationId: ConversationId, online: S.Boolean })),
  }),
  error: Err,
})

// ============================================================================
// Audio calling on AWS Chime. A call is a 2-attendee Chime meeting plus ring
// semantics (spec §20): Chime owns media/ICE/TURN, so there is no signaling token
// and no ICE-server RPC — the browser gets a Chime join payload instead. The ring
// itself rides chat realtime (the /api/chat/stream SSE), not a bespoke socket.
// ============================================================================

/** Start a call: creates the ringing row, opens the Chime room, rings the peers.
 *  Returns the CALLER's join payload — the room is open before anyone rings, so a
 *  ring is never un-answerable. */
export const StartCallRpc = Rpc.make("startCall", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({
    callId: CallId,
    peerUserIds: S.Array(S.String),
    join: MeetingJoinInfo,
  }),
  error: Err,
})

/** Call history for a conversation — backs the "Calls" tab. Participation-scoped
 *  (a non-participant gets an empty list). Newest-first. */
export const ListCallsRpc = Rpc.make("listCalls", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({ calls: S.Array(CallLogView) }),
  error: Err,
})

/** Answer a call: claims it (first answer wins the hunt group) and returns the
 *  Chime join payload. */
export const AnswerCallRpc = Rpc.make("answerCall", {
  payload: S.Struct({ callId: CallId }),
  success: S.Struct({ join: MeetingJoinInfo }),
  error: Err,
})

/** Re-mint a join payload for a LIVE call after the client's Chime session died
 *  under it (e.g. signaling socket closed unexpectedly). Participant-checked; no
 *  claim and no status change; terminal calls fail. Lets the client self-heal a
 *  dropped session by rejoining the same room instead of ending the call. */
export const RejoinCallRpc = Rpc.make("rejoinCall", {
  payload: S.Struct({ callId: CallId }),
  success: S.Struct({ join: MeetingJoinInfo }),
  error: Err,
})

/** Invite a same-org teammate into a LIVE call. Inviter must be a conversation
 *  participant; the pending invite row authorizes the invitee's joinCall.
 *  No BillingLockoutMiddleware — the call family carries none (only messaging
 *  mutations do), and gating call teardown behind a lockout strands peers. */
export const InviteToCallRpc = Rpc.make("inviteToCall", {
  payload: S.Struct({ callId: CallId, inviteeUserId: S.String }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

/** Accept a call invite: mints an attendee into the call's live room.
 *  No hunt-group claim, no status change (the call is already connected). */
export const JoinCallRpc = Rpc.make("joinCall", {
  payload: S.Struct({ callId: CallId }),
  success: S.Struct({ join: MeetingJoinInfo }),
  error: Err,
})

/** Self-leave. Ends the call only when fewer than 2 active participants remain. */
export const LeaveCallRpc = Rpc.make("leaveCall", {
  payload: S.Struct({ callId: CallId }),
  success: S.Struct({ ended: S.Boolean }),
  error: Err,
})

export const DeclineCallRpc = Rpc.make("declineCall", {
  payload: S.Struct({ callId: CallId }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

export const EndCallRpc = Rpc.make("endCall", {
  payload: S.Struct({
    callId: CallId,
    status: S.optional(S.Literal("ended", "missed", "failed")),
    reason: S.optional(S.String),
  }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

// ============================================================================
// Space meetings (AWS Chime SDK). Spaces get MEETINGS people join; 1:1 threads
// get Calls. Chime hosts the room + supplies ICE/TURN, so there is no signaling
// token here — join returns the opaque Chime `{ meeting, attendee }` payload the
// browser SDK consumes. See spec 2026-07-16-002.
// ============================================================================

/** Start an INSTANT meeting in a space. Fails `not_allowed` on a 1:1 thread. */
export const StartMeetingRpc = Rpc.make("startMeeting", {
  payload: S.Struct({
    conversationId: ConversationId,
    title: S.String.pipe(S.minLength(1), S.maxLength(200)),
    transcriptionEnabled: S.optional(S.Boolean),
  }),
  success: S.Struct({ meetingId: MeetingId, join: MeetingJoinInfo }),
  error: Err,
})

/** Join a live meeting. `consent` only matters when the meeting is transcribed —
 *  declining joins listen-only (the client never publishes a mic track, so the
 *  words never reach Transcribe). */
export const JoinMeetingRpc = Rpc.make("joinMeeting", {
  payload: S.Struct({ meetingId: MeetingId, consent: S.optional(S.Boolean) }),
  success: S.Struct({ join: MeetingJoinInfo }),
  error: Err,
})

export const LeaveMeetingRpc = Rpc.make("leaveMeeting", {
  payload: S.Struct({ meetingId: MeetingId }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

/** End the meeting for everyone (deletes the Chime meeting, stopping
 *  transcription with it). HOST ONLY — attendees get `not_allowed`. */
export const EndMeetingRpc = Rpc.make("endMeeting", {
  payload: S.Struct({ meetingId: MeetingId }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

/** Meetings in a space (upcoming / live / past), participation-scoped. */
export const ListMeetingsRpc = Rpc.make("listMeetings", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({ meetings: S.Array(MeetingView) }),
  error: Err,
})

// ---- scheduling (spec 2026-07-26-001 §10) ----------------------------------
// No BillingLockoutMiddleware, consistent with the call/meeting family.

/** Put a meeting in the future. Participation is enough to schedule; only the
 *  organiser can later move or cancel it. */
export const ScheduleMeetingRpc = Rpc.make("scheduleMeeting", {
  payload: S.Struct({
    conversationId: ConversationId,
    title: S.String.pipe(S.minLength(1), S.maxLength(200)),
    description: S.optional(S.NullOr(S.String.pipe(S.maxLength(2000)))),
    /** ISO-8601 UTC. Validated server-side as future — a client clock is not
     *  authority for "is this in the past". */
    scheduledAt: S.String,
    durationMinutes: S.Int.pipe(S.greaterThanOrEqualTo(1), S.lessThanOrEqualTo(480)),
    preset: RecurrencePreset,
    /** Capped so one request can't fan out an unbounded invite list. */
    inviteeUserIds: S.Array(S.String).pipe(S.maxItems(200)),
  }),
  success: S.Struct({
    meetingId: MeetingId,
    invitedCount: S.Number,
    /** Invitees that couldn't be addressed — surfaced so the UI can say so
     *  rather than silently inviting fewer people than were picked. */
    skippedInviteeCount: S.Number,
    occurrencesCreated: S.Number,
  }),
  error: Err,
})

/** Upcoming (future, not cancelled) meetings for a conversation, soonest first. */
export const ListUpcomingMeetingsRpc = Rpc.make("listUpcomingMeetings", {
  payload: S.Struct({ conversationId: ConversationId }),
  success: S.Struct({ meetings: S.Array(ScheduledMeetingView) }),
  error: Err,
})

/** Everything the CALLER is invited to across conversations, for a dashboard. */
export const ListMyUpcomingMeetingsRpc = Rpc.make("listMyUpcomingMeetings", {
  payload: S.Struct({
    withinHours: S.optional(S.Int.pipe(S.greaterThanOrEqualTo(1), S.lessThanOrEqualTo(720))),
  }),
  success: S.Struct({ meetings: S.Array(ScheduledMeetingView) }),
  error: Err,
})

/**
 * Known-busy windows for a conversation's people, to draw the scheduler's
 * availability strip.
 *
 * NOT a free/busy authority. It reports what Cova knows — meetings scheduled
 * here, plus external meetings a user has told us about — and knows nothing of
 * a calendar we don't see. The UI must phrase it as "nothing Cova knows about
 * clashes", never "everyone is available".
 */
export const ListAvailabilityRpc = Rpc.make("listAvailability", {
  payload: S.Struct({
    conversationId: ConversationId,
    /** ISO window, typically the day being scheduled. */
    from: S.String,
    to: S.String,
  }),
  success: S.Struct({
    busy: S.Array(
      S.Struct({
        userId: S.String,
        title: S.String,
        startsAt: S.String,
        endsAt: S.String,
        source: S.Literal("cova", "external"),
      }),
    ),
    /** Everyone the meeting would invite, so the UI can list them AND show who
     *  has no known conflicts — resolved server-side from the conversation. */
    invitees: S.Array(S.Struct({ userId: S.String, name: S.String })),
  }),
  error: Err,
})

// ---- guests (v4.59) --------------------------------------------------------
// The guest's OWN endpoints are REST (/api/meet/[token]) — they have no Clerk
// session. These three are the HOST side, and are authenticated normally.

/** Invite an address with no Cova account. The guest gets this meeting and
 *  nothing else, and still has to be admitted before they can join. */
export const InviteGuestRpc = Rpc.make("inviteGuest", {
  payload: S.Struct({
    meetingId: MeetingId,
    email: S.String.pipe(S.minLength(3), S.maxLength(320)),
  }),
  success: S.Struct({ inviteId: S.String, email: S.String }),
  error: Err,
})

/** Guests waiting at the door of a meeting. Polled while the meeting is live. */
export const ListKnockingGuestsRpc = Rpc.make("listKnockingGuests", {
  payload: S.Struct({ meetingId: MeetingId }),
  success: S.Struct({
    waiting: S.Array(
      S.Struct({
        id: S.String,
        invitedEmail: S.NullOr(S.String),
        knockedAt: S.NullOr(S.String),
      }),
    ),
  }),
  error: Err,
})

/** Open the door. ANY participant may admit — requiring the organiser would
 *  strand a guest whenever the organiser is late. */
export const AdmitGuestRpc = Rpc.make("admitGuest", {
  payload: S.Struct({ meetingId: MeetingId, inviteId: S.String }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

/** Cancel one occurrence or the whole series. Organiser only. */
export const CancelScheduledMeetingRpc = Rpc.make("cancelScheduledMeeting", {
  payload: S.Struct({
    meetingId: MeetingId,
    scope: S.Literal("occurrence", "series"),
  }),
  success: S.Struct({ ok: S.Boolean, scope: S.Literal("occurrence", "series") }),
  error: Err,
})

/** Move a meeting. Organiser only; clears the reminder stamp server-side so the
 *  new time is reminded again. */
export const RescheduleMeetingRpc = Rpc.make("rescheduleMeeting", {
  payload: S.Struct({
    meetingId: MeetingId,
    scheduledAt: S.String,
    durationMinutes: S.Int.pipe(S.greaterThanOrEqualTo(1), S.lessThanOrEqualTo(480)),
  }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

/** Live roster for a meeting (who's in the room right now). */
export const ListMeetingParticipantsRpc = Rpc.make("listMeetingParticipants", {
  payload: S.Struct({ meetingId: MeetingId }),
  success: S.Struct({
    participants: S.Array(S.Struct({ userId: S.String, listenOnly: S.Boolean })),
  }),
  error: Err,
})

/**
 * Relay finalised transcript lines from a participant's browser.
 *
 * The client is the only source: Chime delivers transcript events to attendees
 * over the meeting data channel, and with no recording there is no server-side
 * audio to derive them from. The payload is treated as untrusted — the server
 * stamps meeting/org from the caller's membership and resolves the speaker from
 * Chime's attendee id, so a client cannot attribute words to someone else.
 */
export const AppendMeetingTranscriptRpc = Rpc.make("appendMeetingTranscript", {
  payload: S.Struct({
    meetingId: MeetingId,
    segments: S.Array(TranscriptSegmentInput).pipe(S.minItems(1), S.maxItems(100)),
  }),
  success: S.Struct({ ok: S.Boolean }),
  error: Err,
})

/** A meeting's transcript, in spoken order. Participation-scoped. */
export const GetMeetingTranscriptRpc = Rpc.make("getMeetingTranscript", {
  payload: S.Struct({ meetingId: MeetingId }),
  success: S.Struct({ segments: S.Array(TranscriptSegmentView) }),
  error: Err,
})

/**
 * Mint a short-lived, per-user SCOPED credential for the browser to open its
 * Chime SDK Messaging WebSocket — the realtime ring / message-push transport
 * (replaces the retired SSE + Upstash poll). Provisions the caller's app-instance
 * user + signal channel on demand; the creds are limited to chime:Connect on the
 * caller's own user arn and expire, so the browser reconnects periodically.
 */
export const GetChatRealtimeSessionRpc = Rpc.make("getChatRealtimeSession", {
  payload: S.Struct({}),
  success: S.Struct({
    region: S.String,
    appInstanceArn: S.String,
    userArn: S.String,
    channelArn: S.String,
    accessKeyId: S.String,
    secretAccessKey: S.String,
    sessionToken: S.String,
    expiresAtMs: S.Number,
  }),
  error: Err,
})

export const ChatRpc = RpcGroup.make(
  StartConversationRpc,
  SendMessageRpc,
  ListConversationsRpc,
  ListMessagesRpc,
  MarkReadRpc,
  GetChatUnreadRpc,
  GetAttachmentUrlRpc,
  ListTeammatesRpc,
  CreateGroupRpc,
  RenameGroupRpc,
  AddGroupMembersRpc,
  RemoveGroupMemberRpc,
  LeaveGroupRpc,
  ListGroupMembersRpc,
  SetChatMutedRpc,
  SetChatFavouritedRpc,
  SetChatArchivedRpc,
  ListConversationContentRpc,
  GetConversationProfileRpc,
  GetPresenceRpc,
  GetChatRealtimeSessionRpc,
  StartCallRpc,
  ListCallsRpc,
  AnswerCallRpc,
  RejoinCallRpc,
  InviteToCallRpc,
  JoinCallRpc,
  LeaveCallRpc,
  DeclineCallRpc,
  EndCallRpc,
  StartMeetingRpc,
  JoinMeetingRpc,
  LeaveMeetingRpc,
  EndMeetingRpc,
  ListMeetingsRpc,
  ScheduleMeetingRpc,
  ListUpcomingMeetingsRpc,
  ListMyUpcomingMeetingsRpc,
  ListAvailabilityRpc,
  InviteGuestRpc,
  ListKnockingGuestsRpc,
  AdmitGuestRpc,
  CancelScheduledMeetingRpc,
  RescheduleMeetingRpc,
  ListMeetingParticipantsRpc,
  AppendMeetingTranscriptRpc,
  GetMeetingTranscriptRpc,
)
  .annotateRpcs(AllowedApps, ["platform"])
  .middleware(UserMiddleware)
  .middleware(RequestIdentityMiddleware)
  // Who is acting and who is looking (spec section 4.6, ruling 37). This group
  // reaches a files service, so the two tags are in its handlers' requirements
  // channel. The `.middleware(...)` attachment here and the one layer line in
  // the route are the whole of this change to this group.
  .middleware(AuditActorMiddleware)
  .middleware(FilesViewerMiddleware)
  // The caller's firm and person as `TenancyContext` (004 §6 order; v6.99
  // CHAT-WALL): the keyed conversation, message and unread reads beneath run
  // in a tenant segment as `cova_app` under the participation wall. Its
  // `AuthorisationError` rides the middleware's own failure schema.
  .middleware(TenancyMiddleware)
